import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, Tabs, Table, Tag, Space, Popconfirm, Avatar, Badge, Typography } from "antd";
import { PlusOutlined, PictureOutlined } from "@ant-design/icons";
import moment from "moment";

import Sidebar from "../../components/sidebar/Sidebar";
import Navbar from "../../components/navbar/Navbar";
import CustomizationsPanel from "./CustomizationsPanel";
import CustomPackageRequestsPanel from "./CustomPackageRequestsPanel";
import OrganizerMetricsPanel from "./metrics/OrganizerMetricsPanel";
import ListingMetricsDrawer from "./metrics/ListingMetricsDrawer";

import {
  fetchAllPackages,
  fetchAllQuickTrips,
  deletePackage,
  restorePackage,
  deleteQuickTrip,
  restoreQuickTrip,
} from "../../features/packages/packagesThunk";

const { Title } = Typography;

const money = (price) =>
  price ? `${price.currency || "NGN"} ${Number(price.amount || 0).toLocaleString()}` : "—";

const Packages = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("PACKAGE");
  // the row whose booking metrics are open in the drawer
  const [metricsListing, setMetricsListing] = useState(null);
  const { packages, packagesLoading, quickTrips, quickTripsLoading, customPackageRequests } =
    useSelector((state) => state.packages);

  const pendingRequestCount = (customPackageRequests?.data || []).filter(
    (r) => r.status === "REQUESTED",
  ).length;

  const isPackage = activeTab === "PACKAGE";
  const isListingTab = activeTab === "PACKAGE" || activeTab === "QUICK_TRIP";
  const data = isPackage ? packages : quickTrips;
  const loading = isPackage ? packagesLoading : quickTripsLoading;

  const load = (page = 0, pageSize = 10) => {
    if (isPackage) dispatch(fetchAllPackages({ page, pageSize }));
    else dispatch(fetchAllQuickTrips({ page, pageSize }));
  };

  useEffect(() => {
    if (isListingTab) load(0, 10);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleDelete = async (id) => {
    const action = isPackage ? deletePackage(id) : deleteQuickTrip(id);
    const result = await dispatch(action);
    if (!result.error) load(data?.page, data?.pageSize);
  };

  const handleRestore = async (id) => {
    const action = isPackage ? restorePackage(id) : restoreQuickTrip(id);
    const result = await dispatch(action);
    if (!result.error) load(data?.page, data?.pageSize);
  };

  const columns = [
    {
      title: "Image",
      key: "image",
      width: 64,
      render: (_, record) =>
        record.image_urls?.[0] ? (
          <Avatar shape="square" size="large" src={record.image_urls[0]} />
        ) : (
          <Avatar shape="square" size="large" icon={<PictureOutlined />} />
        ),
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "Destination",
      key: "destination",
      render: (_, record) => record.location?.name || "—",
    },
    {
      title: "Price",
      key: "price",
      render: (_, record) => money(record.price),
    },
    {
      title: isPackage ? "Customizable" : "Duration",
      key: "meta",
      render: (_, record) =>
        isPackage ? (
          <Tag color={record.customizable ? "green" : "default"}>
            {record.customizable ? "Yes" : "No"}
          </Tag>
        ) : (
          `${record.duration ?? "—"} day(s)`
        ),
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) =>
        record.deleted_at ? (
          <Tag color="red">Deleted</Tag>
        ) : (
          <Tag color="blue">Active</Tag>
        ),
    },
    {
      title: "Created",
      key: "created_at",
      render: (_, record) =>
        record.created_at ? moment(record.created_at).format("YYYY-MM-DD") : "—",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            onClick={() =>
              navigate(`/packages/${record.id}/edit?type=${activeTab}`)
            }
          >
            Edit
          </Button>
          <Button size="small" onClick={() => setMetricsListing(record)}>
            Metrics
          </Button>
          {record.deleted_at ? (
            <Popconfirm
              title="Restore this listing?"
              onConfirm={() => handleRestore(record.id)}
            >
              <Button size="small">Restore</Button>
            </Popconfirm>
          ) : (
            <Popconfirm
              title="Delete this listing?"
              onConfirm={() => handleDelete(record.id)}
            >
              <Button size="small" danger>
                Delete
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="home">
      <Sidebar />
      <div className="homeContainer">
        <Navbar />
        <div className="listContainer">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            <Title level={3} style={{ margin: 0 }}>
              Packages & Quick Trips
            </Title>
            {isListingTab && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate(`/packages/new?type=${activeTab}`)}
              >
                Create {isPackage ? "Package" : "Quick Trip"}
              </Button>
            )}
          </div>

          <Tabs
            activeKey={activeTab}
            onChange={(key) => {
              setMetricsListing(null);
              setActiveTab(key);
            }}
            items={[
              { key: "PACKAGE", label: "Packages" },
              { key: "QUICK_TRIP", label: "Quick Trips" },
              { key: "CUSTOMIZATIONS", label: "Customization Options" },
              { key: "ORGANIZER_METRICS", label: "Organizer Metrics" },
              {
                key: "CUSTOM_REQUESTS",
                label: (
                  <Badge count={pendingRequestCount} offset={[8, 0]} size="small">
                    <span>Custom Package Requests</span>
                  </Badge>
                ),
              },
            ]}
          />

          {isListingTab ? (
            <Table
              rowKey="id"
              loading={loading}
              columns={columns}
              dataSource={data?.data || []}
              pagination={{
                current: (data?.page ?? 0) + 1,
                pageSize: data?.pageSize || 10,
                total: data?.total || 0,
                onChange: (page, pageSize) => load(page - 1, pageSize),
              }}
            />
          ) : activeTab === "CUSTOMIZATIONS" ? (
            <CustomizationsPanel />
          ) : activeTab === "ORGANIZER_METRICS" ? (
            <OrganizerMetricsPanel />
          ) : (
            <CustomPackageRequestsPanel />
          )}

          <ListingMetricsDrawer
            listing={metricsListing}
            type={activeTab}
            onClose={() => setMetricsListing(null)}
          />
        </div>
      </div>
    </div>
  );
};

export default Packages;
